// Helper utilities shared across channel-related components.
// Keep logic in sync with ChannelChat so optimistic updates behave identically.
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

export function cloneUser(user) {
  if (!user || typeof user !== 'object') return null;
  const cloned = { ...user };
  if (user.user_image && typeof user.user_image === 'object') {
    cloned.user_image = { ...user.user_image };
  }
  return cloned;
}

export function resolveUserId(value) {
  if (!value) return null;
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  if (typeof value === 'object') {
    return (
      value._id
      || value.id
      || value.user_id
      || value.userId
      || value.member_id
      || value.memberId
      || null
    );
  }
  return null;
}

export function ensureImagePayload(imageLike) {
  if (!imageLike) return null;

  if (typeof imageLike === 'string') {
    const trimmed = imageLike.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('blob:')) {
      return trimmed;
    }
    return { image_id: trimmed };
  }

  if (typeof imageLike === 'object') {
    const directId = imageLike.image_id || imageLike.imageId || imageLike.id || null;
    if (directId) return { image_id: String(directId) };

    if (imageLike.image && typeof imageLike.image === 'object') {
      const nested = ensureImagePayload(imageLike.image);
      if (nested) return nested;
    }

    const url = imageLike.url || imageLike.src || null;
    if (typeof url === 'string' && url.trim()) {
      return url.trim();
    }
  }

  return null;
}

export function resolveImageId(...imageLike) {
  return resolveUserProfileImage(...imageLike);
}

export function normalizeProfileType(...values) {
  return resolveUserProfileTypeWithFallback('collector', ...values);
}

export function resolveChannelOwnerProfileType(value, fallback = 'gallery') {
  return resolveUserProfileTypeWithFallback(fallback, value);
}

export function isSameUser(userA, userB) {
  if (!userA || !userB) return false;
  const idA = resolveUserId(userA);
  const idB = resolveUserId(userB);
  if (idA && idB && String(idA) === String(idB)) return true;
  const usernameA = userA.user_username || userA.username || null;
  const usernameB = userB.user_username || userB.username || null;
  if (usernameA && usernameB) return usernameA === usernameB;
  return false;
}

function pickFirstString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function toFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeMediaPayload(mediaLike) {
  if (!mediaLike) return null;

  if (Array.isArray(mediaLike)) {
    const list = mediaLike.map((item) => normalizeMediaPayload(item)).filter(Boolean);
    if (!list.length) return null;
    if (list.length === 1) return list[0];
    return { type: 'collection', items: list };
  }

  if (typeof mediaLike === 'string') {
    return {
      type: 'image',
      url: mediaLike,
      playbackUrl: mediaLike,
      previewUrl: mediaLike,
      thumbnailUrl: mediaLike,
    };
  }

  if (typeof mediaLike !== 'object') return null;

  const typeCandidate = pickFirstString(mediaLike.type, mediaLike.media_type, mediaLike.kind, mediaLike.category);
  let normalizedType = typeCandidate ? typeCandidate.toLowerCase() : '';

  const playbackUrl = pickFirstString(
    mediaLike.playbackUrl,
    mediaLike.playback_url,
    mediaLike.deliveryUrl,
    mediaLike.delivery_url,
    mediaLike.assetUrl,
    mediaLike.url,
    mediaLike.secure_url,
    mediaLike.streamUrl,
    mediaLike.stream_url,
  );

  const previewUrl = pickFirstString(
    mediaLike.previewUrl,
    mediaLike.preview_url,
    mediaLike.thumbnailUrl,
    mediaLike.thumbnail_url,
    mediaLike.preview,
    mediaLike.image,
    mediaLike.imageUrl,
    mediaLike.image_url,
  );

  const thumbnailUrl = pickFirstString(
    mediaLike.thumbnailUrl,
    mediaLike.thumbnail_url,
    mediaLike.previewThumbnail,
    mediaLike.preview_thumbnail,
    previewUrl,
  );

  const originalUrl = pickFirstString(
    mediaLike.originalUrl,
    mediaLike.original_url,
    mediaLike.sourceUrl,
    mediaLike.source_url,
    playbackUrl,
    previewUrl,
  );

  if (!normalizedType && playbackUrl && playbackUrl.includes('.m3u8')) {
    normalizedType = 'video';
  }

  const payload = {
    type: normalizedType || null,
    assetId: mediaLike.assetId || mediaLike.asset_id || mediaLike.id || null,
    playbackUrl: playbackUrl || null,
    previewUrl: previewUrl || thumbnailUrl || originalUrl || null,
    thumbnailUrl: thumbnailUrl || previewUrl || null,
    originalUrl: originalUrl || null,
    url: playbackUrl || previewUrl || thumbnailUrl || originalUrl || null,
    deliveryUrl: mediaLike.deliveryUrl || mediaLike.delivery_url || null,
    mimeType: mediaLike.mimeType || mediaLike.contentType || mediaLike.mime_type || null,
    width: toFiniteNumber(mediaLike.width || mediaLike.originalWidth || mediaLike.dimensions?.width),
    height: toFiniteNumber(mediaLike.height || mediaLike.originalHeight || mediaLike.dimensions?.height),
    durationSeconds: toFiniteNumber(mediaLike.durationSeconds || mediaLike.duration || mediaLike.duration_seconds),
  };

  if (Array.isArray(mediaLike.previewUrls)) {
    payload.previewUrls = mediaLike.previewUrls.filter((value) => typeof value === 'string');
  }
  if (mediaLike.variants && typeof mediaLike.variants === 'object') {
    payload.variants = mediaLike.variants;
  }
  if (mediaLike.transcodingStatus || mediaLike.transcoding_status) {
    payload.transcodingStatus = mediaLike.transcodingStatus || mediaLike.transcoding_status;
  }

  return payload;
}

export function normalizeChannelMessage(message, { fallbackSender, currentUser } = {}) {
  if (!message || typeof message !== 'object') return null;

  const inferredSender =
    (message.message_sender && typeof message.message_sender === 'object' && message.message_sender)
    || (message.user && typeof message.user === 'object' && message.user)
    || (message.sender && typeof message.sender === 'object' && message.sender)
    || null;

  const senderIdCandidate =
    resolveUserId(message.message_sender)
    || resolveUserId(message.user)
    || resolveUserId(message.sender)
    || resolveUserId(inferredSender)
    || null;

  let sender = inferredSender ? cloneUser(inferredSender) : null;
  const fallback = fallbackSender
    || (currentUser
      && senderIdCandidate
      && resolveUserId(currentUser)
      && String(resolveUserId(currentUser)) === String(senderIdCandidate)
      ? currentUser
      : null);

  if (!sender && fallback) {
    sender = cloneUser(fallback);
  } else if (sender && fallback && !sender.user_image && fallback.user_image) {
    sender.user_image = cloneUser({ user_image: fallback.user_image }).user_image;
  }

  if (!sender && typeof message.message_sender === 'string' && fallback) {
    sender = cloneUser(fallback);
  }

  if (sender && sender.user_image) {
    sender.user_image = ensureImagePayload(sender.user_image) || null;
  }

  // Do not fallback to message-level image fields here. In channel payloads those
  // fields may contain channel cover assets instead of the sender profile image.

  const normalized = {
    ...message,
    message_sender: sender,
    user: sender || fallback || message.user || null,
  };

  normalized.message_text = message.message_text ?? message.text ?? '';
  normalized.text = normalized.message_text;

  const mediaCandidates = [
    message.message_media,
    message.media,
    message.media_payload,
    Array.isArray(message.mediaList) ? message.mediaList : null,
    message.attachment,
  ];

  let normalizedMedia = null;
  for (const candidate of mediaCandidates) {
    const parsed = normalizeMediaPayload(candidate);
    if (parsed) {
      normalizedMedia = parsed;
      break;
    }
  }
  if (normalizedMedia && normalizedMedia.type === 'collection' && Array.isArray(normalizedMedia.items) && normalizedMedia.items.length === 1) {
    normalizedMedia = normalizedMedia.items[0];
  }

  normalized.message_media = normalizedMedia || null;
  normalized.media = normalizedMedia || null;

  const sticker = message.message_sticker || message.sticker || null;
  normalized.message_sticker = sticker || null;

  const explicitType = message.message_type || message.messageType || message.type || null;
  normalized.message_type = explicitType
    || (normalizedMedia ? 'media:' + (normalizedMedia.type || '') : sticker ? 'sticker' : 'text');

  const createdAtCandidate = message.createdAt || message.created_at || message.timestamp || Date.now();
  const createdAtDate = new Date(createdAtCandidate);
  normalized.createdAt = Number.isNaN(createdAtDate.getTime())
    ? new Date().toISOString()
    : createdAtDate.toISOString();

  const updatedAtCandidate = message.updatedAt || message.updated_at || null;
  if (updatedAtCandidate) {
    const updatedAtDate = new Date(updatedAtCandidate);
    normalized.updatedAt = Number.isNaN(updatedAtDate.getTime())
      ? normalized.createdAt
      : updatedAtDate.toISOString();
  }

  normalized._id = message._id || message.id || normalized._id;
  normalized.clientSideId = message.clientSideId || message.client_side_id || normalized.clientSideId || null;
  normalized.channelId = message.channelId || message.channel_id || normalized.channelId || null;

  if (typeof normalized.isMine !== 'boolean' && currentUser) {
    normalized.isMine = isSameUser(normalized.message_sender, currentUser);
  }

  return normalized;
}

function extractReplyContext(message) {
  if (!message || typeof message !== 'object') return null;
  const reply =
    message?.metadata?.reply
    || message?.message_metadata?.reply
    || message?.reply
    || message?.message_reply
    || null;
  return reply && typeof reply === 'object' ? reply : null;
}

export function deriveUnreadMessageIds(messageList, currentUser, readState) {
  if (!Array.isArray(messageList) || !currentUser) return [];
  const currentId = resolveUserId(currentUser);
  const currentUsername = currentUser.user_username || currentUser.username || null;
  if (!currentId && !currentUsername) return [];

  const lastRead = (() => {
    if (!readState) return 0;
    const candidate = readState.lastReadAt || readState.last_read_at || readState.timestamp || null;
    if (!candidate) return 0;
    const parsed = new Date(candidate).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  })();

  return messageList
    .filter((message) => {
      if (!message) return false;
      if (isSameUser(message.message_sender || message.user, currentUser)) return false;

      if (Array.isArray(message.readBy) || Array.isArray(message.read_by)) {
        const readCandidates = (message.readBy || message.read_by).map((entry) => ({
          id: resolveUserId(entry),
          username: entry?.user_username || entry?.username || null,
        }));
        if (readCandidates.some((entry) => {
          if (entry.id && currentId && String(entry.id) === String(currentId)) return true;
          if (entry.username && currentUsername && entry.username === currentUsername) return true;
          return false;
        })) {
          return false;
        }
      }

      const messageCreatedAt = new Date(message.createdAt || message.created_at || Date.now()).getTime();
      if (lastRead && messageCreatedAt <= lastRead) {
        return false;
      }

      if (Array.isArray(message.message_readers)) {
        const readers = message.message_readers.map((entry) => ({
          id: resolveUserId(entry),
          username: entry?.user_username || entry?.username || null,
        }));
        if (readers.some((entry) => {
          if (entry.id && currentId && String(entry.id) === String(currentId)) return true;
          if (entry.username && currentUsername && entry.username === currentUsername) return true;
          return false;
        })) {
          return false;
        }
      }

      const flagKeys = ['viewed', 'is_viewed', 'is_read', 'isRead', 'seen'];
      for (const key of flagKeys) {
        if (typeof message[key] === 'boolean') {
          return message[key] === false;
        }
      }

      return true;
    })
    .map((message) => message._id)
    .filter(Boolean);
}

export function markChannelMessageAsReadLocally(message, currentUserId) {
  if (!message || !currentUserId) return message;
  const updated = { ...message };
  updated.viewed = true;
  updated.is_read = true;
  updated.isRead = true;

  const readArrays = ['readBy', 'read_by', 'viewedBy', 'viewed_by', 'seenBy', 'seen_by'];
  readArrays.forEach((key) => {
    if (!Array.isArray(updated[key])) return;
    const exists = updated[key].some((entry) => String(resolveUserId(entry)) === String(currentUserId));
    if (!exists) {
      updated[key] = [...updated[key], { user_id: currentUserId }];
    }
  });

  return updated;
}

export function buildMessagePreviewText(message) {
  if (!message) return '';

  const sender =
    (message.user && typeof message.user === 'object' && message.user)
    || (message.message_sender && typeof message.message_sender === 'object' && message.message_sender)
    || null;

  const senderName =
    sender?.user_username
    || message?.message_sender_username
    || message?.sender_username
    || message?.user_username
    || '';

  const messageText = typeof message?.message_text === 'string'
    ? message.message_text
    : typeof message?.text === 'string'
      ? message.text
      : '';

  const hasMedia = Boolean(message?.media || message?.message_media);
  const mediaType = (message?.media?.type || message?.message_media?.type || '').toLowerCase();
  const isSticker = Boolean(message?.message_sticker);

  let baseText = '';
  if (hasMedia) {
    if (mediaType === 'video') baseText = 'Video';
    else if (mediaType === 'audio') baseText = 'Audio';
    else baseText = 'Imagen';
  } else if (isSticker) {
    baseText = 'Sticker';
  } else {
    baseText = messageText.trim();
  }

  if (!baseText) {
    baseText = hasMedia ? 'Archivo' : '';
  }

  if (!baseText && !senderName) return '';

  const combined = senderName ? `${senderName}: ${baseText}` : baseText;
  return combined.length > 80 ? `${combined.slice(0, 77)}...` : combined;
}

export function getMessageIdentifier(message) {
  if (!message) return null;
  const candidates = [message._id, message.id, message.message_id, message.clientSideId];
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === 'string' || typeof candidate === 'number') {
      return candidate;
    }
    if (typeof candidate === 'object') {
      const nested = getMessageIdentifier(candidate);
      if (nested) return nested;
    }
  }
  return null;
}

export function getTimeValue(value) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

export function areSummaryEntriesEqual(prev, next) {
  if (!prev && !next) return true;
  if (!prev || !next) return false;
  const prevMessageId = getMessageIdentifier(prev.lastMessage);
  const nextMessageId = getMessageIdentifier(next.lastMessage);
  if (prevMessageId !== nextMessageId) return false;
  const prevUnread = prev?.unreadCount ?? 0;
  const nextUnread = next?.unreadCount ?? 0;
  if (prevUnread !== nextUnread) return false;
  const prevActivity = getTimeValue(prev?.lastActivity);
  const nextActivity = getTimeValue(next?.lastActivity);
  if (prevActivity !== nextActivity) return false;
  const prevReadAt = getTimeValue(prev?.readState?.lastReadAt);
  const nextReadAt = getTimeValue(next?.readState?.lastReadAt);
  if (prevReadAt !== nextReadAt) return false;
  return true;
}

export function mergeMessages(existing, incoming, currentUser) {
  const map = new Map();
  const result = [];

  existing.forEach((message) => {
    const normalized = normalizeChannelMessage(message, { currentUser });
    if (!normalized) return;
    const key = getMessageIdentifier(normalized);
    if (key && !map.has(key)) {
      map.set(key, normalized);
    }
    result.push(normalized);
  });

  incoming.forEach((message) => {
    const normalized = normalizeChannelMessage(message, { currentUser });
    if (!normalized) return;
    const key = getMessageIdentifier(normalized);
    if (key && map.has(key)) {
      const index = result.findIndex((item) => getMessageIdentifier(item) === key);
      if (index !== -1) {
        const previousItem = result[index];
        const previousReply = extractReplyContext(result[index]);
        const nextReply = extractReplyContext(normalized);
        if (previousItem?.clientSideId && !normalized.clientSideId) {
          normalized.clientSideId = previousItem.clientSideId;
        }
        if (previousReply && !nextReply) {
          normalized.metadata = {
            ...(normalized.metadata && typeof normalized.metadata === 'object' ? normalized.metadata : {}),
            reply: previousReply,
          };
        }
        result[index] = normalized;
      }
      map.set(key, normalized);
    } else {
      result.push(normalized);
      if (key) {
        map.set(key, normalized);
      }
    }
  });

  result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  return result;
}
